import { useIntegrations } from "@api/integration";
import useActiveGym from "@ui/hooks/active-gym";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { useHeliosPagesFeature } from "@/lib/feature-flags/use-feature-flag";
import type { AnnouncementContext, FeatureAnnouncementEntry } from "./registry";
import { useAnnouncementLifecycle } from "./shared";
import { announcementShownThisSession } from "./store";
import { announcementForced, useAnnouncement } from "./use-announcement";

/**
 * App-level announcement modal, mounted once in MainLayout so a major
 * release reaches users no matter which screen they land on. Gathers its own
 * audience context (feature flags, the gym's active integrations), then lets
 * the registry decide what, if anything, opens.
 *
 * Frequency cap: at most one modal per session. An announcement that was
 * shown but not engaged with this session waits for the next one instead of
 * reopening on every navigation.
 */
export function FeatureAnnouncementDialog() {
  const { gym } = useActiveGym();
  const { enabled: pagesEnabled, isLoading: pagesLoading } = useHeliosPagesFeature();
  const { data: integrations } = useIntegrations(
    { gym: gym?.id },
    { continue: () => Boolean(gym?.id) },
  );

  const ready = Boolean(gym?.id) && !pagesLoading && integrations !== undefined;
  const context = useMemo<AnnouncementContext>(() => {
    const active = (integrations ?? []).filter((integration) => integration.active);
    return {
      pagesEnabled,
      integrations: active.map((integration) => integration.type),
      mindbodyExpandedWebhookEvents: active.some(
        (integration) =>
          integration.type === "mindbody" && integration.options?.expandedWebhookEvents === true,
      ),
    };
  }, [pagesEnabled, integrations]);

  const { announcement, dismiss } = useAnnouncement({
    display: "modal",
    placement: "app",
    context,
    ready,
  });

  // Open after a short delay so the screen settles first, and only when this
  // announcement has not already been shown this session (a QA override
  // skips the session cap so the modal can be replayed on demand).
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!announcement) return;
    if (announcementShownThisSession(announcement.id) && !announcementForced(announcement.id)) {
      return;
    }
    const timeout = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(timeout);
  }, [announcement]);

  if (!announcement || !open) return null;

  return <AnnouncementModal announcement={announcement} onDismiss={dismiss} />;
}

function AnnouncementModal(props: {
  announcement: FeatureAnnouncementEntry;
  onDismiss: () => void;
}) {
  const { announcement } = props;
  const lifecycle = useAnnouncementLifecycle(announcement, props.onDismiss);
  const Illustration = announcement.illustration;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) lifecycle.dismiss();
      }}
    >
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        {Illustration ? <Illustration /> : null}
        <div className="p-6">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary dark:text-brand-accent">
            New
          </p>
          <DialogTitle className="mt-1.5">{announcement.title}</DialogTitle>
          <DialogDescription className="mt-2">{announcement.body}</DialogDescription>
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={lifecycle.dismiss}>
              {announcement.secondaryLabel ?? "Not now"}
            </Button>
            <Button onClick={lifecycle.complete}>{announcement.primaryAction.label}</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
